// src/Components/Modules/HR/RequisitionForm.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  TextField, Button, Chip, IconButton, Tooltip,
  Skeleton, Alert, Stack, Avatar, MenuItem,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider,
} from "@mui/material";
import {
  FaPlus, FaBoxOpen, FaClock, FaCheckCircle, FaTimesCircle,
  FaSearch, FaSync, FaEye, FaInfoCircle,
  FaExclamationTriangle, FaPaperPlane, FaBuilding,
  FaBan,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const FormCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: "1rem",
  fontWeight: 700,
  borderRadius: theme.spacing(2),
  textTransform: "none",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "&:hover": {
    background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
  },
}));

const RequisitionForm = () => {
  const [form, setForm] = useState({
    item_name: "",
    requested_quantity: 1,
    unit: "pcs",
    purpose: "",
    urgency_level: "medium",
    department: "",
  });
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialog, setViewDialog] = useState(null);

  useEffect(() => {
    fetchRequisitions();
    fetchEmployeeName();
  }, []);

  const fetchEmployeeName = async () => {
    try {
      const res = await AxiosAuthInstance.get("hr/employee/me");
      const emp = res.data.employee || {};
      if (emp.department) {
        setForm(prev => ({ ...prev, department: emp.department }));
      }
    } catch (err) {
      console.warn("Could not fetch employee info");
    }
  };

  const fetchRequisitions = async () => {
    setLoadingData(true);
    try {
      const res = await AxiosAuthInstance.get("hr/requisitions/my");
      setRequisitions(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      console.error("Failed to fetch requisitions", err);
      setRequisitions([]);
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.item_name.trim()) newErrors.item_name = "Item name is required";
    if (!form.requested_quantity || form.requested_quantity < 1) {
      newErrors.requested_quantity = "Quantity must be at least 1";
    }
    if (!form.purpose.trim() || form.purpose.trim().length < 5) {
      newErrors.purpose = "Purpose must be at least 5 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const result = await Swal.fire({
      title: "Submit Requisition?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Item:</strong> ${form.item_name}</p>
          <p><strong>Quantity:</strong> ${form.requested_quantity} ${form.unit}</p>
          <p><strong>Urgency:</strong> ${form.urgency_level}</p>
          <p><strong>Purpose:</strong> ${form.purpose}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      await AxiosAuthInstance.post("hr/requisitions", form);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Requisition submitted successfully",
        timer: 2000,
        showConfirmButton: false,
      });
      setForm({
        item_name: "",
        requested_quantity: 1,
        unit: "pcs",
        purpose: "",
        urgency_level: "medium",
        department: form.department,
      });
      setErrors({});
      fetchRequisitions();
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to submit requisition";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelRequisition = async (id) => {
    const result = await Swal.fire({
      title: "Cancel Requisition?",
      text: "Are you sure you want to cancel this requisition?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      await AxiosAuthInstance.post(`hr/requisitions/${id}/cancel`);
      Swal.fire({
        icon: "success",
        title: "Cancelled!",
        text: "Requisition has been cancelled",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchRequisitions();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to cancel",
      });
    }
  };

  const filtered = useMemo(() => {
    return requisitions.filter((item) => {
      const itemName = item.item_name?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();
      const matchesSearch = itemName.includes(searchTerm);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requisitions, search, statusFilter]);

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const stats = useMemo(() => {
    return {
      total: requisitions.length,
      pending: requisitions.filter(r => r.status === "pending").length,
      approved: requisitions.filter(r => r.status === "approved").length,
      fulfilled: requisitions.filter(r => r.status === "fulfilled").length,
      rejected: requisitions.filter(r => r.status === "rejected").length,
    };
  }, [requisitions]);

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    const configs = {
      pending: { icon: <FaClock />, label: "Pending", bg: "#fff3e0", color: "#ef6c00", iconColor: "#ff9800" },
      approved: { icon: <FaCheckCircle />, label: "Approved", bg: "#e8f5e9", color: "#2e7d32", iconColor: "#4caf50" },
      fulfilled: { icon: <FaCheckCircle />, label: "Fulfilled", bg: "#e3f2fd", color: "#1565c0", iconColor: "#2196f3" },
      rejected: { icon: <FaTimesCircle />, label: "Rejected", bg: "#ffebee", color: "#c62828", iconColor: "#f44336" },
      cancelled: { icon: <FaBan />, label: "Cancelled", bg: "#f5f5f5", color: "#757575", iconColor: "#9e9e9e" },
    };
    const config = configs[statusLower] || configs.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bg,
          color: config.color,
          fontWeight: 600,
          "& .MuiChip-icon": { color: config.iconColor },
        }}
      />
    );
  };

  const getUrgencyChip = (urgency) => {
    const configs = {
      high: { bg: "#ffebee", color: "#c62828", iconColor: "#f44336" },
      medium: { bg: "#fff3e0", color: "#ef6c00", iconColor: "#ff9800" },
      low: { bg: "#e8f5e9", color: "#2e7d32", iconColor: "#4caf50" },
    };
    const config = configs[urgency?.toLowerCase()] || configs.medium;
    return (
      <Chip
        icon={<FaExclamationTriangle style={{ fontSize: 12 }} />}
        label={urgency || "Medium"}
        size="small"
        sx={{
          bgcolor: config.bg,
          color: config.color,
          fontWeight: 600,
          textTransform: "capitalize",
          "& .MuiChip-icon": { color: config.iconColor },
        }}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (loadingData && requisitions.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="My Requisitions" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="My Requisitions" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaBoxOpen style={{ marginRight: 10, verticalAlign: "middle" }} />
              My Requisitions
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Request office supplies and track your requests
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchRequisitions}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={0}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#e3f2fd", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaBoxOpen size={22} color="#2196f3" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#1565c0">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={0}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#fff3e0", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaClock size={22} color="#ff9800" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#ef6c00">
              {stats.pending}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Pending
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={0}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaCheckCircle size={22} color="#4caf50" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#2e7d32">
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Approved
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={0}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#e3f2fd", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaCheckCircle size={22} color="#2196f3" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#1565c0">
              {stats.fulfilled}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Fulfilled
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard elevation={0}>
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#ffebee", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaTimesCircle size={22} color="#f44336" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#c62828">
              {stats.rejected}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Rejected
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Submit Form */}
      <FormCard sx={{ mb: 3 }}>
        <Box sx={{ p: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
          <Typography variant="h6" fontWeight={600}>
            <FaPlus style={{ marginRight: 8, verticalAlign: "middle" }} />
            Submit New Requisition
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Request office supplies from admin
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Item Name *"
                placeholder="e.g., Ballpoint Pen, A4 Paper"
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                error={!!errors.item_name}
                helperText={errors.item_name}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: "#757575", display: "flex" }}>
                      <FaBoxOpen />
                    </Box>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Quantity *"
                type="number"
                value={form.requested_quantity}
                onChange={(e) => setForm({ ...form, requested_quantity: Number(e.target.value) })}
                error={!!errors.requested_quantity}
                helperText={errors.requested_quantity}
                inputProps={{ min: 1 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                <MenuItem value="pcs">Pcs</MenuItem>
                <MenuItem value="box">Box</MenuItem>
                <MenuItem value="ream">Ream</MenuItem>
                <MenuItem value="set">Set</MenuItem>
                <MenuItem value="pack">Pack</MenuItem>
                <MenuItem value="kg">Kg</MenuItem>
                <MenuItem value="liter">Liter</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Urgency"
                value={form.urgency_level}
                onChange={(e) => setForm({ ...form, urgency_level: e.target.value })}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                <MenuItem value="low">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FaExclamationTriangle style={{ color: "#4caf50", fontSize: 12 }} />
                    Low
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FaExclamationTriangle style={{ color: "#ff9800", fontSize: 12 }} />
                    Medium
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FaExclamationTriangle style={{ color: "#f44336", fontSize: 12 }} />
                    High
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Department"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: "#757575", display: "flex" }}>
                      <FaBuilding />
                    </Box>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose *"
                multiline
                rows={3}
                placeholder="Why do you need this item? (minimum 5 characters)"
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                error={!!errors.purpose}
                helperText={errors.purpose || `${form.purpose.length}/5 characters minimum`}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setForm({
                      item_name: "",
                      requested_quantity: 1,
                      unit: "pcs",
                      purpose: "",
                      urgency_level: "medium",
                      department: form.department,
                    });
                    setErrors({});
                  }}
                  sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}
                >
                  Clear
                </Button>
                <SubmitButton
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading ? null : <FaPaperPlane />}
                >
                  {loading ? "Submitting..." : "Submit Requisition"}
                </SubmitButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </FormCard>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by item name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: "#757575", display: "flex" }}>
                  <FaSearch />
                </Box>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="fulfilled">Fulfilled</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={60}>SL</StyledTableCell>
              <StyledTableCell>Item</StyledTableCell>
              <StyledTableCell>Qty</StyledTableCell>
              <StyledTableCell>Urgency</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Submitted</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaBoxOpen style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No requisitions found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Submit your first requisition using the form above"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const statusLower = row.status?.toLowerCase();
                const canCancel = statusLower === "pending" || statusLower === "approved";
                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", width: 36, height: 36 }}>
                          <FaBoxOpen size={14} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {row.item_name}
                          </Typography>
                          {row.department && (
                            <Typography variant="caption" color="text.secondary">
                              {row.department}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${row.requested_quantity} ${row.unit || "pcs"}`}
                        size="small"
                        sx={{
                          bgcolor: "#f5f5f5",
                          color: "#424242",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{getUrgencyChip(row.urgency_level)}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(row.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => setViewDialog(row)}
                            sx={{ color: "#2196f3" }}
                          >
                            <FaEye fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canCancel && (
                          <Tooltip title="Cancel Request">
                            <IconButton
                              size="small"
                              onClick={() => cancelRequisition(row.id)}
                              sx={{ color: "#f44336" }}
                            >
                              <FaBan fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": { paddingRight: 2 },
            }}
          />
        )}
      </StyledTableContainer>

      {/* View Dialog */}
      <Dialog
        open={!!viewDialog}
        onClose={() => setViewDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white", py: 2.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaInfoCircle />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Requisition Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewDialog && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Item</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewDialog.item_name}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Requested</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {viewDialog.requested_quantity} {viewDialog.unit || "pcs"}
                  </Typography>
                </Grid>
                {viewDialog.approved_quantity && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Approved</Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {viewDialog.approved_quantity} {viewDialog.unit || "pcs"}
                    </Typography>
                  </Grid>
                )}
                {viewDialog.fulfilled_quantity && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Fulfilled</Typography>
                    <Typography variant="body1" fontWeight={600} color="primary">
                      {viewDialog.fulfilled_quantity} {viewDialog.unit || "pcs"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {viewDialog.approved_quantity !== viewDialog.requested_quantity && viewDialog.approved_quantity && (
                <Alert severity="info" icon={<FaInfoCircle />}>
                  Admin approved <strong>{viewDialog.approved_quantity}</strong> out of <strong>{viewDialog.requested_quantity}</strong> requested
                </Alert>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Urgency</Typography>
                {getUrgencyChip(viewDialog.urgency_level)}
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Department</Typography>
                <Typography variant="body1">{viewDialog.department || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Purpose</Typography>
                <Typography variant="body1">{viewDialog.purpose}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {getStatusChip(viewDialog.status)}
              </Box>
              {viewDialog.rejection_reason && (
                <Alert severity="error" icon={<FaTimesCircle />}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Rejection Reason:
                  </Typography>
                  <Typography variant="body2">{viewDialog.rejection_reason}</Typography>
                </Alert>
              )}
              {viewDialog.admin_notes && (
                <Alert severity="info" icon={<FaInfoCircle />}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Admin Notes:
                  </Typography>
                  <Typography variant="body2">{viewDialog.admin_notes}</Typography>
                </Alert>
              )}
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Submitted: {formatDate(viewDialog.created_at)}
                </Typography>
                {viewDialog.approved_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {viewDialog.status === "approved" || viewDialog.status === "fulfilled" ? "Approved" : "Processed"}: {formatDate(viewDialog.approved_at)}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setViewDialog(null)} variant="contained" sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequisitionForm;